import { useRef, useState, useEffect, useMemo, useCallback, type ChangeEvent } from "react";
import { printPrzInvoicesPDF } from "@/features/finances/utils/printPrzInvoicesPDF";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { PageLoader } from "@/components/ui/Spinner";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import apiClient from "@/api/client";
import { useAuthStore } from "@/store/auth";
import { Button } from "@/components/ui/Button";
import { DatePicker } from "@/components/ui/DatePicker";
import { Badge } from "@/components/ui/Badge";
import { formatAmount } from "@/utils/formatAmount";
import { ClientInvoiceAiBadge } from "@/features/finances/components/ClientInvoiceAiPanel";
import { naturalSortKey } from "@/utils/naturalSort";
import {
  FilterMultiSelect as MultiSelect,
  type FilterOption as MSOption,
} from "@/components/ui/FilterMultiSelect";
import {
  SheetCalcProvider,
  SheetCalcToggleButton,
  CalcCell,
} from "@/features/finances/components/SheetCalc";
import { usePrzProformaToConvertCount } from "@/features/invoices/hooks/useInvoices";
import {
  useClientInvoices,
  useClientInvoiceLineItems,
  useCreateClientInvoice,
  useDeleteClientInvoice,
  useUpdateClientInvoice,
  useUpdateClientInvoicePayment,
  useApprovePrzDraft,
  useRevokeApprovalPrzDraft,
} from "@/features/finances/hooks/useFinances";
import type { ClientInvoice } from "@/features/finances/types";
import {
  PAYMENT_STATUS_LABELS,
  PAYMENT_STATUS_COLORS,
  CLIENT_INVOICE_STATUS_LABELS,
  CLIENT_INVOICE_STATUS_COLORS,
  INVOICE_TYPE_LABELS,
  PAYMENT_TERM_DAY_OPTIONS,
  type ClientInvoiceType,
} from "@/features/finances/types";

interface Project {
  id: string;
  name: string;
  short_name: string;
  company_name?: string | null;
  client: string | null;
  client_detail: { id: string; name: string; short_name: string } | null;
}
interface GKCompany {
  id: string;
  short_code: string;
  name: string;
}

const NO_PROJECT = "__no_project__";

// ── Filter types & helpers ─────────────────────────────────────────────────

interface Filters {
  projects: string[];
  companies: string[];
  invoiceDateFrom: string;
  invoiceDateTo: string;
  paymentDueFrom: string;
  paymentDueTo: string;
  paymentStatuses: string[];
  statuses: string[];
  invoiceTypes: string[];
  // Free-text search — backend matches ?search= against invoice_number + description.
  search: string;
  ordering: string;
}

// Filters survive F5 / returning next day — restored verbatim from localStorage,
// falling back to the defaults (current month) only when nothing was saved yet.
// Keys are per-user: localStorage is shared browser-wide and a plain logout does
// not wipe it, so a fixed key would show one user's filters (and the calculator's
// cached amounts) to whoever logs in next on the same machine.
const filtersStorageKey = (userId: string | undefined) =>
  `elg5.przInvoices.filters.v1.${userId ?? "anon"}`;
const sheetCalcStorageKey = (userId: string | undefined) =>
  `elg5.przInvoices.sheetCalc.v1.${userId ?? "anon"}`;

function loadStoredFilters(storageKey: string): Filters {
  const defaults = getDefaultFilters();
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return defaults;
    return { ...defaults, ...(JSON.parse(raw) as Partial<Filters>) };
  } catch {
    return defaults;
  }
}

function getDefaultFilters(): Filters {
  const now = new Date();
  const firstOfMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
  return {
    projects: [],
    companies: [],
    invoiceDateFrom: firstOfMonth,
    invoiceDateTo: "",
    paymentDueFrom: "",
    paymentDueTo: "",
    paymentStatuses: [],
    statuses: [],
    invoiceTypes: [],
    search: "",
    ordering: "newest",
  };
}

function filtersToParams(f: Filters): Record<string, string> {
  const p: Record<string, string> = { ordering: f.ordering };
  if (f.projects.length) p.projects = f.projects.join(",");
  if (f.companies.length) p.companies = f.companies.join(",");
  if (f.invoiceDateFrom) p.invoice_date_from = f.invoiceDateFrom;
  if (f.invoiceDateTo) p.invoice_date_to = f.invoiceDateTo;
  if (f.paymentDueFrom) p.payment_due_from = f.paymentDueFrom;
  if (f.paymentDueTo) p.payment_due_to = f.paymentDueTo;
  if (f.paymentStatuses.length)
    p.payment_statuses = f.paymentStatuses.join(",");
  if (f.statuses.length) p.statuses = f.statuses.join(",");
  if (f.invoiceTypes.length) p.invoice_types = f.invoiceTypes.join(",");
  if (f.search.trim()) p.search = f.search.trim();
  return p;
}

const ORDERING_OPTIONS = [
  { value: "newest", label: "Od najnowszych" },
  { value: "oldest", label: "Od najstarszych" },
  { value: "payment_due_asc", label: "Termin płatności ↑" },
  { value: "payment_due_desc", label: "Termin płatności ↓" },
  { value: "amount_asc", label: "Kwota rosnąco" },
  { value: "amount_desc", label: "Kwota malejąco" },
];

const PAY_STATUS_OPTIONS: MSOption[] = [
  { value: "unpaid", label: "Nieopłacona" },
  { value: "partially_paid", label: "Opłacona częściowo" },
  { value: "paid", label: "Opłacona" },
];

const INV_STATUS_OPTIONS: MSOption[] = [
  { value: "draft_unapproved", label: "Szkic — niezatwierdzony" },
  { value: "draft_approved", label: "Szkic — zatwierdzony" },
  { value: "issued", label: "Wystawiona" },
];

const INV_TYPE_OPTIONS: MSOption[] = [
  { value: "regular", label: "Faktura PRZ" },
  { value: "proforma", label: "Proforma" },
  { value: "proforma_advance", label: "Proforma zaliczkowa" },
];

const emptyForm = {
  invoice_number: "",
  amount_net: "",
  amount_gross: "",
  invoice_date: "",
  payment_terms_days: 14,
  description: "",
  is_advance_invoice: false,
  invoice_type: "regular" as ClientInvoiceType,
};

/** Returns which actions are available given current invoice state. */
function availableActions(inv: ClientInvoice, role?: string) {
  const draft = inv.status === "draft";
  const issued = inv.status === "issued";
  const paid = inv.payment_status === "paid";
  const approved = inv.director_approved === true;
  return {
    issue: draft && approved,
    issueBlocked: draft && !approved,
    approve: draft && role === "director" && !approved,
    revokeApproval: draft && role === "director" && approved,
    revertToDraft: issued && !paid && !inv.sent_to_investor,
    uploadFile: issued,
    markSentToInvestor: issued && !inv.sent_to_investor,
    revertSentToInvestor:
      issued && inv.sent_to_investor && inv.payment_status === "unpaid",
    payment: issued && !paid,
    delete: draft,
  };
}

const chip = "text-xs px-2.5 py-1 rounded-md border font-medium transition-colors";

const TODAY = new Date().toISOString().slice(0, 10);

interface TodoTag {
  label: string;
  className: string;
}

function invTodo(inv: ClientInvoice, role?: string, isCeo?: boolean): TodoTag | null {
  const draft = inv.status === "draft";
  const issued = inv.status === "issued";
  const approved = inv.director_approved === true;
  const canIssue = role === "accountant" || (role === "director" && isCeo);

  if (draft && !approved && role === "director")
    return { label: "Zatwierdź", className: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800" };
  if (draft && approved && canIssue)
    return { label: "Wystaw FV", className: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 border-violet-200 dark:border-violet-800" };
  if (draft && approved && role === "director")
    return { label: "Oczekuje KSG", className: "bg-violet-50 text-violet-500 dark:bg-violet-900/20 dark:text-violet-400 border-violet-100 dark:border-violet-900" };
  if (issued && !inv.sent_to_investor && role === "director")
    return { label: "Wyślij do inwestora", className: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-800" };
  if (inv.is_proforma && inv.payment_status === "paid" && (role === "accountant" || (role === "director" && isCeo)))
    return { label: "Przekształć w FV", className: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300 border-orange-200 dark:border-orange-800" };
  return null;
}

function invRowBg(inv: ClientInvoice): string {
  if (inv.payment_status === "paid") return "bg-green-100 dark:bg-green-900/25";
  if (inv.payment_status === "partially_paid") return "bg-green-50 dark:bg-green-900/10";
  if (inv.status === "issued" && inv.payment_due_date && inv.payment_due_date < TODAY)
    return "bg-red-50 dark:bg-red-900/15";
  return "bg-white dark:bg-gray-900/0";
}

function fmtDate(d: string | null | undefined): string {
  if (!d) return "—";
  const [y, m, day] = d.split("-");
  return `${day}.${m}.${y}`;
}

/** Gray "(X z JV)" note shown under our-part amounts when a JV partner share is included. */
function jvFullNote(ours: number, full: number) {
  if (full <= ours + 0.001) return null;
  return (
    <span className="block text-[10px] font-normal text-gray-400" title="Łącznie z częścią partnerów JV">
      ({formatAmount(full.toFixed(2))} z JV)
    </span>
  );
}

/**
 * All money figures for one invoice in both "our share" and full (incl. JV
 * partner) variants. For non-consortium invoices the two are equal, so the
 * "ours" values collapse to the full ones and no JV note is shown.
 *
 * Paid & remaining split the client payment proportionally to our share of the
 * full invoice (collected fraction = paid_amount / amount_gross), mirroring the
 * paid_net = paid × net/gross conversion already used across the page. Keeping
 * row cells and the RAZEM footer on this single source prevents them drifting
 * apart (the previous bug: gross/net showed our part while VAT/remaining showed
 * the full incl-JV amount).
 */
function invAmounts(inv: ClientInvoice) {
  const gross = parseFloat(inv.amount_gross);
  const net = parseFloat(inv.amount_net);
  const ourGross = parseFloat(inv.our_gross_share || inv.amount_gross);
  const ourNet = parseFloat(inv.our_net_share || inv.amount_net);
  const paidGross = parseFloat(inv.paid_amount || "0");
  const f = gross > 0 ? paidGross / gross : 0; // collected fraction of the full invoice
  return {
    gross, net, ourGross, ourNet,
    vatFull: gross - net,
    vatOurs: ourGross - ourNet,
    paidGrossFull: paidGross,
    paidGrossOurs: ourGross * f,
    paidNetFull: net * f,
    paidNetOurs: ourNet * f,
    remainingFull: net - net * f,
    remainingOurs: ourNet - ourNet * f,
  };
}

function InlineLineItems({ invoiceId }: { invoiceId: string }) {
  const { data: items = [], isLoading } = useClientInvoiceLineItems(invoiceId);
  if (isLoading)
    return (
      <div className="border-t border-gray-200 dark:border-gray-700 px-5 py-2 bg-gray-50 dark:bg-gray-800/50 text-xs text-gray-400">
        Wczytywanie pozycji…
      </div>
    );
  if (items.length === 0) return null;
  return (
    <div className="border-t border-gray-200 dark:border-gray-700 border-l-4 border-l-indigo-200 dark:border-l-indigo-800 bg-gray-50 dark:bg-gray-900/40 py-3">
      {/* Mobile compact list */}
      <div className="sm:hidden px-4 space-y-1.5">
        {items.map((item) => (
          <div key={item.id} className="flex items-baseline justify-between gap-3 text-xs">
            <span className="text-gray-600 dark:text-gray-400 leading-snug min-w-0">
              {item.position}. {item.name}
              <span className="text-gray-400 ml-1">× {item.quantity}</span>
            </span>
            <span className="text-gray-900 dark:text-gray-100 font-semibold tabular-nums shrink-0">
              {formatAmount(item.total)} PLN
            </span>
          </div>
        ))}
      </div>
      {/* Desktop table */}
      <div className="hidden sm:block px-5 overflow-x-auto">
        <table className="w-full text-xs min-w-[380px]">
          <thead>
            <tr className="text-gray-500 dark:text-gray-400 border-b border-gray-300 dark:border-gray-600">
              <th className="text-left pb-1.5 pr-3 w-8 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Lp.</th>
              <th className="text-left pb-1.5 pr-3 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Opis</th>
              <th className="text-right pb-1.5 pr-3 w-16 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Ilość</th>
              <th className="text-right pb-1.5 pr-3 w-32 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Cena jedn.</th>
              <th className="text-right pb-1.5 w-32 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Razem</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {items.map((item) => (
              <tr key={item.id} className="text-gray-700 dark:text-gray-300">
                <td className="py-1.5 pr-3 text-gray-400 tabular-nums">{item.position}</td>
                <td className="py-1.5 pr-3">{item.name}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums">{item.quantity}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums">{formatAmount(item.unit_value)} PLN</td>
                <td className="py-1.5 text-right tabular-nums font-semibold text-gray-900 dark:text-gray-100">{formatAmount(item.total)} PLN</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function ClientInvoicesPage() {
  const user = useAuthStore((s) => s.user);
  const canEdit = user?.role === "director" || user?.role === "accountant";
  const isAccountant = user?.role === "accountant";

  const { data: proformaToConvertCount = 0 } = usePrzProformaToConvertCount(isAccountant);

  const filtersKey = filtersStorageKey(user?.id);
  const [filters, setFilters] = useState<Filters>(() => loadStoredFilters(filtersKey));
  const [viewMode, setViewMode] = useState<"cards" | "table">("table");

  useEffect(() => {
    try {
      localStorage.setItem(filtersKey, JSON.stringify(filters));
    } catch {
      // Storage unavailable — filters simply won't survive a reload.
    }
  }, [filtersKey, filters]);
  const [form, setForm] = useState({ ...emptyForm, project: "" });
  const [formCompany, setFormCompany] = useState("");
  const [formClient, setFormClient] = useState("");
  const [formClientName, setFormClientName] = useState("");
  const [formClientNip, setFormClientNip] = useState("");
  const [formClientFilter, setFormClientFilter] = useState("");
  const [formErr, setFormErr] = useState<string | null>(null);

  // Filter options — all projects & companies from backend (not limited to current invoices)
  const { data: filterOptions } = useQuery({
    queryKey: ["client-invoices-filter-options"],
    queryFn: () =>
      apiClient
        .get<{
          data: {
            projects: Project[];
            companies: { short_code: string; name: string }[];
          };
        }>("/finances/client-invoices/filter-options/")
        .then((r) => r.data.data),
  });

  // Separate project fetch for the "new invoice" form (unchanged)
  const { data: projects = [], isLoading: projectsLoading } = useQuery({
    queryKey: ["projects"],
    queryFn: () =>
      apiClient.get<{ data: Project[] }>("/projects/").then((r) => r.data.data),
  });

  const { data: clientsList = [] } = useQuery<
    { id: string; name: string; short_name: string }[]
  >({
    queryKey: ["clients-brief"],
    queryFn: () =>
      apiClient
        .get<{
          data: { id: string; name: string; short_name: string }[];
        }>("/clients/")
        .then((r) => r.data.data),
    enabled: canEdit,
  });

  const { data: gkCompanies = [] } = useQuery<GKCompany[]>({
    queryKey: ["gk-companies"],
    queryFn: () =>
      apiClient
        .get<{ data: GKCompany[] }>("/group-finance/companies/")
        .then((r) => r.data.data),
    enabled: canEdit,
  });

  const sortedProjects = useMemo(() => {
    const filtered = formClientFilter
      ? projects.filter((p) => p.client === formClientFilter)
      : projects;
    return [...filtered].sort((a, b) =>
      naturalSortKey(a.short_name).localeCompare(naturalSortKey(b.short_name), "pl"),
    );
  }, [projects, formClientFilter]);

  const formClients = useMemo(() => {
    const seen = new Map<string, { id: string; name: string; short_name: string }>();
    for (const p of projects) {
      if (p.client && p.client_detail && !seen.has(p.client)) {
        seen.set(p.client, p.client_detail);
      }
    }
    return [...seen.values()].sort((a, b) =>
      naturalSortKey(a.short_name || a.name).localeCompare(naturalSortKey(b.short_name || b.name), "pl"),
    );
  }, [projects]);

  const projectOptions: MSOption[] = (filterOptions?.projects ?? []).map(
    (p) => ({
      value: p.id,
      label: `${p.short_name} — ${p.name}`,
    }),
  );

  const companyOptions: MSOption[] = (filterOptions?.companies ?? []).map(
    (c) => ({
      value: c.short_code,
      label: c.short_code,
    }),
  );

  const setF = <K extends keyof Filters>(key: K, val: Filters[K]) =>
    setFilters((f) => ({ ...f, [key]: val }));

  // Debounce only the free-text search so typing a number doesn't fire a request per
  // keystroke; every other filter still applies immediately.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(filters.search), 300);
    return () => clearTimeout(t);
  }, [filters.search]);

  const invoiceParams = useMemo(
    () => filtersToParams({ ...filters, search: debouncedSearch }),
    [filters, debouncedSearch],
  );
  const { data: invoices = [], isLoading } = useClientInvoices(invoiceParams);

  type SortCol =
    | "company" | "invoice_number" | "invoice_date" | "client"
    | "amount_gross" | "amount_net" | "vat"
    | "paid_gross" | "paid_net" | "remaining" | "payment_due_date";

  const [tableSort, setTableSort] = useState<{ col: SortCol; dir: "asc" | "desc" }>({
    col: "invoice_date",
    dir: "asc",
  });

  const sortedInvoices = useMemo(() => {
    const { col, dir } = tableSort;
    const mult = dir === "asc" ? 1 : -1;
    return [...invoices].sort((a, b) => {
      let va: string | number, vb: string | number;
      const ga = parseFloat(a.amount_gross), gb = parseFloat(b.amount_gross);
      const na = parseFloat(a.amount_net),   nb = parseFloat(b.amount_net);
      const pa = parseFloat(a.paid_amount || "0"), pb = parseFloat(b.paid_amount || "0");
      switch (col) {
        case "company":        va = a.company_name ?? ""; vb = b.company_name ?? ""; break;
        case "invoice_number": va = a.invoice_number;     vb = b.invoice_number;     break;
        case "invoice_date":   va = a.invoice_date ?? ""; vb = b.invoice_date ?? ""; break;
        case "client":         va = (a.client_name ?? a.project_name ?? "").toLowerCase(); vb = (b.client_name ?? b.project_name ?? "").toLowerCase(); break;
        case "amount_gross":   va = ga; vb = gb; break;
        case "amount_net":     va = na; vb = nb; break;
        case "vat":            va = ga - na; vb = gb - nb; break;
        case "paid_gross":     va = pa; vb = pb; break;
        case "paid_net":       va = ga > 0 ? pa * na / ga : 0; vb = gb > 0 ? pb * nb / gb : 0; break;
        case "remaining":      va = ga - pa; vb = gb - pb; break;
        case "payment_due_date": va = a.payment_due_date ?? ""; vb = b.payment_due_date ?? ""; break;
        default:               va = ""; vb = "";
      }
      if (va < vb) return -1 * mult;
      if (va > vb) return  1 * mult;
      return 0;
    });
  }, [invoices, tableSort]);

  const toggleSort = (col: SortCol) =>
    setTableSort((s) =>
      s.col === col ? { col, dir: s.dir === "asc" ? "desc" : "asc" } : { col, dir: "asc" },
    );

  // Separate unfiltred query for monthly stats table
  const { data: allInvoicesForStats = [] } = useClientInvoices({});

  const STATS_START_YEAR = 2026;
  const STATS_START_MONTH = 5;

  const monthlyStats = useMemo(() => {
    const MONTHS_PL = [
      "styczeń","luty","marzec","kwiecień","maj","czerwiec",
      "lipiec","sierpień","wrzesień","październik","listopad","grudzień",
    ];
    const now = new Date();
    // Include any month that has an invoice, even if it's in the future
    const maxInvoiceKey = allInvoicesForStats.reduce((max, inv) => {
      const key = inv.invoice_date?.slice(0, 7) ?? "";
      return key > max ? key : max;
    }, `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
    const [endY, endM] = maxInvoiceKey.split("-").map(Number);
    const rows: {
      key: string; label: string;
      issued: number; issuedFull: number;
      issuedPlusApproved: number; issuedPlusApprovedFull: number;
      total: number; totalFull: number;
    }[] = [];
    let y = STATS_START_YEAR, m = STATS_START_MONTH;
    while (
      y < endY ||
      (y === endY && m <= endM)
    ) {
      const key = `${y}-${String(m).padStart(2, "0")}`;
      const monthInvs = allInvoicesForStats.filter((inv) =>
        inv.invoice_date?.startsWith(key),
      );
      // OUR part (net of JV partner share) is primary; full incl. JV kept alongside.
      const sum = (arr: typeof allInvoicesForStats) =>
        arr.reduce((acc, inv) => acc + parseFloat(inv.our_net_share || inv.amount_net || "0"), 0);
      const sumFull = (arr: typeof allInvoicesForStats) =>
        arr.reduce((acc, inv) => acc + parseFloat(inv.amount_net || "0"), 0);
      const issuedInvs = monthInvs.filter((inv) => inv.status === "issued");
      const approvedInvs = monthInvs.filter((inv) => inv.status === "issued" || inv.director_approved === true);
      rows.push({
        key,
        label: `${MONTHS_PL[m - 1]} ${y}`,
        issued: sum(issuedInvs), issuedFull: sumFull(issuedInvs),
        issuedPlusApproved: sum(approvedInvs), issuedPlusApprovedFull: sumFull(approvedInvs),
        total: sum(monthInvs), totalFull: sumFull(monthInvs),
      });
      m++;
      if (m > 12) { m = 1; y++; }
    }
    return rows;
  }, [allInvoicesForStats]);

  const isNoProject = form.project === NO_PROJECT;
  const createMutation = useCreateClientInvoice(
    isNoProject ? undefined : form.project || undefined,
  );
  const deleteMutation = useDeleteClientInvoice();
  const updateMutation = useUpdateClientInvoice();
  const updatePayment = useUpdateClientInvoicePayment();
  const approveMutation = useApprovePrzDraft();
  const revokeMutation = useRevokeApprovalPrzDraft();

  const [issueModal, setIssueModal] = useState<{
    invoice: ClientInvoice;
  } | null>(null);
  const [issueFile, setIssueFile] = useState<File | null>(null);
  const [issueNumber, setIssueNumber] = useState("");

  const [paymentModal, setPaymentModal] = useState<{
    invoice: ClientInvoice;
  } | null>(null);
  const [payForm, setPayForm] = useState({
    new_payment_amount: "",
    payment_date: "",
    payment_comment: "",
  });

  const [editingDesc, setEditingDesc] = useState<{ id: string; value: string } | null>(null);
  const [landscapeModalOpen, setLandscapeModalOpen] = useState(false);
  const [draftPanelOpen, setDraftPanelOpen] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileTargetId, setFileTargetId] = useState<string | null>(null);

  const handleNetChange = (val: string) => {
    const net = parseFloat(val);
    const gross = isNaN(net)
      ? ""
      : (Math.round(net * 1.23 * 100) / 100).toString();
    setForm({ ...form, amount_net: val, amount_gross: gross });
  };

  const handleCreate = async () => {
    if (!form.project) {
      setFormErr(
        "Wybierz przedsięwzięcie lub opcję 'Niepowiązane z przedsięwzięciem'.",
      );
      return;
    }
    if (isNoProject && !formCompany) {
      setFormErr("Wybierz spółkę.");
      return;
    }
    if (!form.amount_net || !form.invoice_date) {
      setFormErr("Kwota netto i data wystawienia są wymagane.");
      return;
    }
    if (isNoProject) {
      if (!formClient) {
        if (!formClientName.trim()) {
          setFormErr("Podaj nazwę firmy klienta.");
          return;
        }
        if (!formClientNip.trim()) {
          setFormErr("Podaj NIP klienta.");
          return;
        }
      }
    }
    try {
      await createMutation.mutateAsync({
        ...(isNoProject ? { company: formCompany } : { project: form.project }),
        ...(isNoProject && formClient ? { client: formClient } : {}),
        ...(isNoProject && !formClient
          ? {
              client_name_override: formClientName.trim(),
              client_nip: formClientNip.trim(),
            }
          : {}),
        invoice_number: form.invoice_number,
        amount_net: form.amount_net,
        amount_gross: form.amount_gross,
        invoice_date: form.invoice_date,
        payment_terms_days: form.payment_terms_days,
        description: form.description,
        is_advance_invoice: form.is_advance_invoice,
        invoice_type: form.invoice_type,
      });
      setForm({ ...emptyForm, project: form.project });
      if (isNoProject) setFormCompany(formCompany);
      setFormErr(null);
    } catch {
      setFormErr("Błąd zapisu faktury.");
    }
  };

  const openPayment = (inv: ClientInvoice) => {
    setPayForm({
      new_payment_amount: "",
      payment_date: "",
      payment_comment: "",
    });
    setPaymentModal({ invoice: inv });
  };

  const handleUpdatePayment = async () => {
    if (!paymentModal) return;
    await updatePayment.mutateAsync({
      id: paymentModal.invoice.id,
      data: payForm,
    });
    setPaymentModal(null);
  };

  const handleFileClick = (id: string) => {
    setFileTargetId(id);
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !fileTargetId) return;
    updateMutation.mutate({ id: fileTargetId, data: { invoice_file: file } });
    setFileTargetId(null);
    e.target.value = "";
  };

  const handleIssue = () => {
    if (!issueModal) return;
    const num = issueNumber.trim() || issueModal.invoice.invoice_number;
    const data: Record<string, unknown> = {
      status: "issued",
      invoice_number: num,
    };
    if (issueFile) data.invoice_file = issueFile;
    updateMutation.mutate(
      { id: issueModal.invoice.id, data },
      {
        onSuccess: () => {
          setIssueModal(null);
          setIssueFile(null);
          setIssueNumber("");
        },
      },
    );
  };

  const EMPTY_FILTERS: Filters = {
    projects: [], companies: [],
    invoiceDateFrom: "", invoiceDateTo: "",
    paymentDueFrom: "", paymentDueTo: "",
    paymentStatuses: [], statuses: [],
    invoiceTypes: [],
    search: "",
    ordering: "newest",
  };

  const handlePrint = useCallback(() => {
    const resolve = (ids: string[], opts: MSOption[]) =>
      ids.map(id => opts.find(o => o.value === id)?.label ?? id).join(", ");
    const fmtIso = (iso: string) => {
      if (!iso) return "—";
      const [y, m, d] = iso.split("-");
      return `${d}.${m}.${y}`;
    };

    const labelMap: { label: string; value: string }[] = [];
    if (filters.projects.length)
      labelMap.push({ label: "Przedsięwzięcia", value: resolve(filters.projects, projectOptions) });
    if (filters.companies.length)
      labelMap.push({ label: "Spółki", value: resolve(filters.companies, companyOptions) });
    if (filters.invoiceTypes.length)
      labelMap.push({ label: "Typ dokumentu", value: resolve(filters.invoiceTypes, INV_TYPE_OPTIONS) });
    if (filters.statuses.length)
      labelMap.push({ label: "Status FV", value: resolve(filters.statuses, INV_STATUS_OPTIONS) });
    if (filters.paymentStatuses.length)
      labelMap.push({ label: "Płatność", value: resolve(filters.paymentStatuses, PAY_STATUS_OPTIONS) });
    if (filters.invoiceDateFrom || filters.invoiceDateTo)
      labelMap.push({ label: "Data wystawienia", value: `${fmtIso(filters.invoiceDateFrom)} — ${fmtIso(filters.invoiceDateTo)}` });
    if (filters.paymentDueFrom || filters.paymentDueTo)
      labelMap.push({ label: "Termin płatności", value: `${fmtIso(filters.paymentDueFrom)} — ${fmtIso(filters.paymentDueTo)}` });
    if (filters.search.trim())
      labelMap.push({ label: "Nr faktury", value: filters.search.trim() });
    if (filters.ordering !== "newest")
      labelMap.push({ label: "Sortowanie", value: ORDERING_OPTIONS.find(o => o.value === filters.ordering)?.label ?? filters.ordering });

    const today = new Date();
    const generatedAt = `${String(today.getDate()).padStart(2, "0")}.${String(today.getMonth() + 1).padStart(2, "0")}.${today.getFullYear()}`;
    printPrzInvoicesPDF({ invoices: sortedInvoices, filterLabels: labelMap, generatedAt });
  }, [filters, sortedInvoices, projectOptions, companyOptions]);

  const invoicesTable = (
    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
      <table className="w-full text-xs min-w-[900px]">
        <thead>
          {(() => {
            const Th = ({
              col, label, right,
            }: {
              col: SortCol; label: string; right?: boolean;
            }) => {
              const active = tableSort.col === col;
              return (
                <th
                  onClick={() => toggleSort(col)}
                  className={`px-3 py-2.5 font-semibold whitespace-nowrap cursor-pointer select-none ${right ? "text-right" : "text-left"} hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${active ? "text-[var(--color-primary)]" : ""}`}
                >
                  <span className="inline-flex items-center gap-0.5">
                    {label}
                    <span className="text-[10px] ml-0.5 w-3 inline-block text-center">
                      {active ? (tableSort.dir === "asc" ? "↑" : "↓") : <span className="text-gray-300 dark:text-gray-600">↕</span>}
                    </span>
                  </span>
                </th>
              );
            };
            return (
              <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
                <th className="px-3 py-2.5 font-semibold text-left whitespace-nowrap">TODO</th>
                <Th col="company"          label="Spółka" />
                <Th col="invoice_number"   label="Nr FV" />
                <Th col="invoice_date"     label="Data wyst." />
                <Th col="client"           label="Nabywca / Projekt" />
                <Th col="amount_gross"     label="Brutto"       right />
                <Th col="amount_net"       label="Netto"        right />
                <Th col="vat"              label="VAT"          right />
                <Th col="paid_gross"       label="Zapł. brutto" right />
                <Th col="paid_net"         label="Zapł. netto"  right />
                <Th col="remaining"        label="Pozostaje"    right />
                <Th col="payment_due_date" label="Termin płatn." />
                <th className="px-3 py-2.5 font-semibold text-left whitespace-nowrap">Opis</th>
              </tr>
            );
          })()}
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
          {sortedInvoices.map((inv, rowIdx) => {
            const amt = invAmounts(inv);
            const bg = invRowBg(inv);
            const calcCell = (col: number, value: number) => ({
              id: `fv:${inv.id}:${col}`,
              tableId: "fv",
              index: rowIdx,
              col,
              value: Number(value.toFixed(2)),
              currency: "PLN",
            });
            return (
              <tr key={inv.id} className={`${bg} hover:brightness-95 transition-all`}>
                <td className="px-3 py-2 whitespace-nowrap">
                  {(() => {
                    const todo = invTodo(inv, user?.role, user?.is_ceo);
                    if (!todo) return null;
                    return (
                      <span className={`text-[10px] px-2 py-0.5 rounded border font-semibold ${todo.className}`}>
                        {todo.label}
                      </span>
                    );
                  })()}
                </td>
                <td className="px-3 py-2 font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                  {inv.company_name ?? "—"}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <Link
                    to={`/finances/invoices-prz/${inv.id}`}
                    className="text-[var(--color-primary)] hover:underline font-medium"
                  >
                    {inv.invoice_number || (
                      inv.director_approved
                        ? <span className="italic text-blue-500 dark:text-blue-400 font-normal">(szkic — zatwierdzony)</span>
                        : <span className="italic text-gray-400 dark:text-gray-500 font-normal">(szkic — niezatwierdzony)</span>
                    )}
                  </Link>
                  {/* Werdykt AI pliku faktury — backend dosyła go tylko przy dostępie do finansów. */}
                  {inv.ai_analysis && (
                    <span className="ml-1">
                      <ClientInvoiceAiBadge analysis={inv.ai_analysis} />
                    </span>
                  )}
                  {inv.is_advance_invoice && (
                    <span className="ml-1 text-[9px] px-1 py-px rounded bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 font-semibold">ZAL</span>
                  )}
                  {inv.invoice_type === "proforma" && (
                    <span className="ml-1 text-[9px] px-1 py-px rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 font-semibold">PRF</span>
                  )}
                  {inv.invoice_type === "proforma_advance" && (
                    <span className="ml-1 text-[9px] px-1 py-px rounded bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300 font-semibold">PRF ZAL</span>
                  )}
                  {inv.is_consortium && (
                    <span
                      className="ml-1 text-[9px] px-1 py-px rounded bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 font-semibold"
                      title={`Konsorcjum — nasza część netto: ${formatAmount(inv.our_net_share)} PLN`}
                    >
                      JV
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-gray-600 dark:text-gray-400">
                  {fmtDate(inv.invoice_date)}
                  {inv.fv_vat_date && (
                    <span className="ml-1 text-[10px] text-gray-400 dark:text-gray-500" title="Data FV VAT (podstawa podatkowa)">
                      (FV: {fmtDate(inv.fv_vat_date)})
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-gray-600 dark:text-gray-400 max-w-[180px] truncate" title={inv.client_name ?? inv.project_name ?? "—"}>
                  {inv.client_name ?? inv.project_name ?? "—"}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300 whitespace-nowrap">
                  <CalcCell {...calcCell(0, parseFloat(inv.our_gross_share || inv.amount_gross))}>
                    {parseFloat(inv.our_gross_share) < parseFloat(inv.amount_gross) - 0.001 ? (
                      <>
                        {formatAmount(inv.our_gross_share)}
                        <span className="block text-[10px] font-normal text-gray-400">({formatAmount(inv.amount_gross)} z JV)</span>
                      </>
                    ) : (
                      formatAmount(inv.amount_gross)
                    )}
                  </CalcCell>
                </td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold text-gray-800 dark:text-gray-200 whitespace-nowrap">
                  <CalcCell {...calcCell(1, parseFloat(inv.our_net_share || inv.amount_net))}>
                    {parseFloat(inv.our_net_share) < parseFloat(inv.amount_net) - 0.001 ? (
                      <>
                        {formatAmount(inv.our_net_share)}
                        <span className="block text-[10px] font-normal text-gray-400">({formatAmount(inv.amount_net)} z JV)</span>
                      </>
                    ) : (
                      formatAmount(inv.amount_net)
                    )}
                  </CalcCell>
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-500 dark:text-gray-400 whitespace-nowrap">
                  <CalcCell {...calcCell(2, amt.vatOurs)}>
                    {formatAmount(amt.vatOurs.toFixed(2))}
                    {jvFullNote(amt.vatOurs, amt.vatFull)}
                  </CalcCell>
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300 whitespace-nowrap">
                  <CalcCell {...calcCell(3, Math.max(0, amt.paidGrossOurs))}>
                    {amt.paidGrossOurs > 0 ? (
                      <>
                        {formatAmount(amt.paidGrossOurs.toFixed(2))}
                        {jvFullNote(amt.paidGrossOurs, amt.paidGrossFull)}
                      </>
                    ) : (
                      <span className="text-gray-300 dark:text-gray-600">—</span>
                    )}
                  </CalcCell>
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300 whitespace-nowrap">
                  <CalcCell {...calcCell(4, Math.max(0, amt.paidNetOurs))}>
                    {amt.paidNetOurs > 0 ? (
                      <>
                        {formatAmount(amt.paidNetOurs.toFixed(2))}
                        {jvFullNote(amt.paidNetOurs, amt.paidNetFull)}
                      </>
                    ) : (
                      <span className="text-gray-300 dark:text-gray-600">—</span>
                    )}
                  </CalcCell>
                </td>
                <td className={`px-3 py-2 text-right tabular-nums font-semibold whitespace-nowrap ${amt.remainingOurs > 0.005 ? "text-red-600 dark:text-red-400" : "text-green-600 dark:text-green-400"}`}>
                  <CalcCell {...calcCell(5, amt.remainingOurs > 0.005 ? amt.remainingOurs : 0)}>
                    {amt.remainingOurs > 0.005 ? (
                      <>
                        {formatAmount(amt.remainingOurs.toFixed(2))}
                        {jvFullNote(amt.remainingOurs, amt.remainingFull)}
                      </>
                    ) : (
                      <span className="text-green-500">✓</span>
                    )}
                  </CalcCell>
                </td>
                <td className={`px-3 py-2 whitespace-nowrap ${inv.payment_due_date && inv.payment_due_date < TODAY && inv.payment_status !== "paid" ? "text-red-600 dark:text-red-400 font-semibold" : "text-gray-600 dark:text-gray-400"}`}>
                  {fmtDate(inv.payment_due_date)}
                </td>
                <td className="px-3 py-2 max-w-[200px]">
                  {editingDesc?.id === inv.id ? (
                    <input
                      autoFocus
                      className="w-full text-xs border rounded px-1.5 py-1 bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      value={editingDesc.value}
                      onChange={(e) => setEditingDesc({ id: inv.id, value: e.target.value })}
                      onBlur={() => {
                        updateMutation.mutate({ id: inv.id, data: { description: editingDesc.value } });
                        setEditingDesc(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") e.currentTarget.blur();
                        if (e.key === "Escape") setEditingDesc(null);
                      }}
                    />
                  ) : (
                    <span
                      className="block truncate text-xs text-gray-600 dark:text-gray-400 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400"
                      title={inv.description || "Kliknij, aby dodać opis"}
                      onClick={() => setEditingDesc({ id: inv.id, value: inv.description ?? "" })}
                    >
                      {inv.description || <span className="text-gray-300 dark:text-gray-600 italic">—</span>}
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 font-semibold text-gray-700 dark:text-gray-300">
            <td className="px-3 py-2.5 text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400" colSpan={5}>
              Razem ({invoices.length} FV)
            </td>
            {(() => {
              // Footer totals live one virtual row below the body so a Shift+click
              // range over data rows never silently swallows the RAZEM cell.
              const totalCalcCell = (col: number, value: number) => ({
                id: `fv:total:${col}`,
                tableId: "fv",
                index: sortedInvoices.length,
                col,
                value: Number(value.toFixed(2)),
                currency: "PLN",
              });
              const grossFull = invoices.reduce((s, i) => s + parseFloat(i.amount_gross), 0);
              const grossOurs = invoices.reduce((s, i) => s + parseFloat(i.our_gross_share || i.amount_gross), 0);
              const netFull = invoices.reduce((s, i) => s + parseFloat(i.amount_net), 0);
              const netOurs = invoices.reduce((s, i) => s + parseFloat(i.our_net_share || i.amount_net), 0);
              const vatOurs = invoices.reduce((s, i) => s + invAmounts(i).vatOurs, 0);
              const vatFull = invoices.reduce((s, i) => s + invAmounts(i).vatFull, 0);
              const paidGrossOurs = invoices.reduce((s, i) => s + invAmounts(i).paidGrossOurs, 0);
              const paidGrossFull = invoices.reduce((s, i) => s + invAmounts(i).paidGrossFull, 0);
              const paidNetOurs = invoices.reduce((s, i) => s + invAmounts(i).paidNetOurs, 0);
              const paidNetFull = invoices.reduce((s, i) => s + invAmounts(i).paidNetFull, 0);
              const remainingOurs = invoices.reduce((s, i) => s + Math.max(0, invAmounts(i).remainingOurs), 0);
              const remainingFull = invoices.reduce((s, i) => s + Math.max(0, invAmounts(i).remainingFull), 0);
              return (
                <>
                  <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">
                    <CalcCell {...totalCalcCell(0, grossOurs)}>
                      {formatAmount(grossOurs.toFixed(2))}
                      {grossOurs < grossFull - 0.001 && (
                        <span className="block text-[10px] font-normal text-gray-400" title="Łącznie z częścią partnerów JV">
                          ({formatAmount(grossFull.toFixed(2))} z JV)
                        </span>
                      )}
                    </CalcCell>
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">
                    <CalcCell {...totalCalcCell(1, netOurs)}>
                      {formatAmount(netOurs.toFixed(2))}
                      {netOurs < netFull - 0.001 && (
                        <span className="block text-[10px] font-normal text-gray-400" title="Łącznie z częścią partnerów JV">
                          ({formatAmount(netFull.toFixed(2))} z JV)
                        </span>
                      )}
                    </CalcCell>
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap text-gray-500 dark:text-gray-400">
                    <CalcCell {...totalCalcCell(2, vatOurs)}>
                      {formatAmount(vatOurs.toFixed(2))}{jvFullNote(vatOurs, vatFull)}
                    </CalcCell>
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">
                    <CalcCell {...totalCalcCell(3, paidGrossOurs)}>
                      {formatAmount(paidGrossOurs.toFixed(2))}{jvFullNote(paidGrossOurs, paidGrossFull)}
                    </CalcCell>
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">
                    <CalcCell {...totalCalcCell(4, paidNetOurs)}>
                      {formatAmount(paidNetOurs.toFixed(2))}{jvFullNote(paidNetOurs, paidNetFull)}
                    </CalcCell>
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap text-red-600 dark:text-red-400">
                    <CalcCell {...totalCalcCell(5, remainingOurs)}>
                      {formatAmount(remainingOurs.toFixed(2))}{jvFullNote(remainingOurs, remainingFull)}
                    </CalcCell>
                  </td>
                </>
              );
            })()}
            <td />
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  );

  return (
    <SheetCalcProvider storageKey={sheetCalcStorageKey(user?.id)}>
    <div>
      {/* Page header */}
      <div className="flex items-start justify-between gap-2 mb-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
            Faktury PRZ
          </h1>
          <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-4 sm:mb-6" />
        </div>
        <div className="flex items-center gap-2">
          <SheetCalcToggleButton size="sm" />
          <button
            className="text-xs px-2.5 sm:px-3 py-1.5 rounded-md font-medium bg-gray-600 hover:bg-gray-700 text-white transition-colors whitespace-nowrap"
            onClick={handlePrint}
            title="Eksportuj aktualnie wyfiltrowaną listę do PDF (A4 poziomo)"
          >
            Drukuj PDF
          </button>
          <button
            className="text-xs px-2.5 sm:px-3 py-1.5 rounded-md font-medium bg-red-600 hover:bg-red-700 text-white transition-colors whitespace-nowrap"
            onClick={() => setFilters(EMPTY_FILTERS)}
          >
            Wyczyść filtry
          </button>
        </div>
      </div>

      {/* Proforma-to-convert todo banner — accountant only */}
      {isAccountant && proformaToConvertCount > 0 && (
        <div className="mb-4 flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
          <span className="mt-0.5 text-amber-500 shrink-0">⚠</span>
          <div>
            <span className="font-semibold">
              {proformaToConvertCount === 1
                ? "1 proforma opłacona"
                : `${proformaToConvertCount} proformy opłacone`}{" "}
              — wymagane wystawienie faktury VAT.
            </span>
            <span className="ml-1 text-amber-700 dark:text-amber-300">
              Przefiltruj listę według typu dokumentu i wejdź w szczegóły każdej opłaconej proformy, aby ją przekształcić.
            </span>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div className="bg-gray-50 dark:bg-gray-800/50 p-2 sm:p-3 mb-4 sm:mb-5 flex flex-col gap-2">
        <div className="flex flex-wrap gap-2 items-center">
          <MultiSelect
            label="Przedsięwzięcia"
            options={projectOptions}
            selected={filters.projects}
            onChange={(v) => setF("projects", v)}
          />
          <MultiSelect
            label="Spółki"
            options={companyOptions}
            selected={filters.companies}
            onChange={(v) => setF("companies", v)}
          />
          <MultiSelect
            label="Typ dokumentu"
            options={INV_TYPE_OPTIONS}
            selected={filters.invoiceTypes}
            onChange={(v) => setF("invoiceTypes", v)}
          />
          <MultiSelect
            label="Status FV"
            options={INV_STATUS_OPTIONS}
            selected={filters.statuses}
            onChange={(v) => setF("statuses", v)}
          />
          <MultiSelect
            label="Płatność"
            options={PAY_STATUS_OPTIONS}
            selected={filters.paymentStatuses}
            onChange={(v) => setF("paymentStatuses", v)}
          />
          <div className="ml-auto">
            <select
              value={filters.ordering}
              onChange={(e) => setF("ordering", e.target.value)}
              className="border border-gray-300 dark:border-gray-600 rounded-md px-2.5 py-1.5 text-xs bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200"
            >
              {ORDERING_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:gap-3">
          <input
            type="text"
            value={filters.search}
            onChange={(e) => setF("search", e.target.value)}
            placeholder="Szukaj nr faktury…"
            aria-label="Szukaj po numerze faktury"
            className="w-full sm:w-52 border border-gray-300 dark:border-gray-600 rounded px-2.5 py-1.5 text-xs bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200"
          />
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-1.5">
            <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
              Wystawiona:
            </span>
            <div className="flex items-center gap-1.5">
              <DatePicker
                value={filters.invoiceDateFrom}
                onChange={(v) => setF("invoiceDateFrom", v)}
                className="flex-1 sm:flex-none"
              />
              <span className="text-xs text-gray-400">—</span>
              <DatePicker
                value={filters.invoiceDateTo}
                onChange={(v) => setF("invoiceDateTo", v)}
                className="flex-1 sm:flex-none"
              />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-1.5">
            <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
              Termin płatności:
            </span>
            <div className="flex items-center gap-1.5">
              <DatePicker
                value={filters.paymentDueFrom}
                onChange={(v) => setF("paymentDueFrom", v)}
                className="flex-1 sm:flex-none"
              />
              <span className="text-xs text-gray-400">—</span>
              <DatePicker
                value={filters.paymentDueTo}
                onChange={(v) => setF("paymentDueTo", v)}
                className="flex-1 sm:flex-none"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 items-start">
        {/* Left panel — add form */}
        {canEdit && (
          <div className="w-full lg:w-72 lg:flex-shrink-0">
            <button
              type="button"
              onClick={() => setDraftPanelOpen((v) => !v)}
              className="lg:pointer-events-none w-full flex items-center justify-between gap-2 mb-2 lg:mb-4"
            >
              <h2 className="text-base font-bold text-gray-900 dark:text-gray-100 text-left">
                Nowy szkic faktury PRZ
              </h2>
              <svg
                className={`w-5 h-5 text-gray-500 transition-transform lg:hidden ${draftPanelOpen ? "rotate-180" : ""}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div className={`${draftPanelOpen ? "flex" : "hidden"} lg:flex bg-[var(--color-primary-dim)] dark:bg-[var(--color-primary-hover)] border border-[var(--color-primary)] rounded-lg p-3 lg:p-4 flex-col gap-2 lg:gap-3`}>
              {projectsLoading ? (
                <div className="flex flex-col gap-2">
                  <div className="h-4 w-24 rounded bg-gray-200 dark:bg-gray-600 animate-pulse" />
                  <div className="h-8 w-full rounded bg-gray-200 dark:bg-gray-600 animate-pulse" />
                  <div className="h-4 w-20 rounded bg-gray-200 dark:bg-gray-600 animate-pulse" />
                  <div className="h-8 w-full rounded bg-gray-200 dark:bg-gray-600 animate-pulse" />
                </div>
              ) : (
              <>
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                  Klient (filtr przedsięwzięć)
                </label>
                <select
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                  value={formClientFilter}
                  onChange={(e) => {
                    setFormClientFilter(e.target.value);
                    setForm({ ...form, project: "" });
                    setFormCompany("");
                    setFormClient("");
                    setFormClientName("");
                    setFormClientNip("");
                  }}
                >
                  <option value="">— wszyscy klienci —</option>
                  {formClients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.short_name || c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                  Przedsięwzięcie *
                </label>
                <select
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                  value={form.project}
                  onChange={(e) => {
                    setForm({ ...form, project: e.target.value });
                    setFormCompany("");
                    setFormClient("");
                    setFormClientName("");
                    setFormClientNip("");
                  }}
                >
                  <option value="">— wybierz —</option>
                  <option value={NO_PROJECT}>
                    Niepowiązane z przedsięwzięciem
                  </option>
                  {sortedProjects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.short_name} — {p.name}
                    </option>
                  ))}
                </select>
                {isNoProject ? (
                  <div className="mt-2">
                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                      Spółka GK *
                    </label>
                    <select
                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                      value={formCompany}
                      onChange={(e) => setFormCompany(e.target.value)}
                    >
                      <option value="">— wybierz spółkę —</option>
                      {gkCompanies.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.short_code} — {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : form.project ? (
                  (() => {
                    const co = projects.find(
                      (p) => p.id === form.project,
                    )?.company_name;
                    return co ? (
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Spółka:{" "}
                        <span className="font-medium text-gray-700 dark:text-gray-300">
                          {co}
                        </span>
                      </p>
                    ) : null;
                  })()
                ) : null}
                {isNoProject && (
                  <div className="mt-2 flex flex-col gap-2">
                    <div>
                      <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                        Klient (inwestor)
                      </label>
                      <select
                        className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                        value={formClient}
                        onChange={(e) => {
                          setFormClient(e.target.value);
                          if (e.target.value) {
                            setFormClientName("");
                            setFormClientNip("");
                          }
                        }}
                      >
                        <option value="">— wpisz ręcznie —</option>
                        {clientsList.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.short_name || c.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    {!formClient && (
                      <>
                        <div>
                          <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                            Nazwa firmy klienta{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                            placeholder="np. PGE Dystrybucja S.A."
                            value={formClientName}
                            onChange={(e) => setFormClientName(e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                            NIP klienta <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                            placeholder="np. 123-456-78-90"
                            value={formClientNip}
                            onChange={(e) => setFormClientNip(e.target.value)}
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
              </>
              )}
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                  Nr faktury
                </label>
                <input
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                  value={form.invoice_number}
                  onChange={(e) =>
                    setForm({ ...form, invoice_number: e.target.value })
                  }
                  placeholder="np. FV/2026/001"
                />
                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                  {form.invoice_number.trim()
                    ? "Numer podany — zostanie zapisana jako wystawiona FV."
                    : "Bez numeru — zostanie zapisana jako szkic."}
                </p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                  Data wystawienia *
                </label>
                <DatePicker
                  value={form.invoice_date}
                  onChange={(v) =>
                    setForm({ ...form, invoice_date: v })
                  }
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                  Kwota netto (PLN) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                  value={form.amount_net}
                  onChange={(e) => handleNetChange(e.target.value)}
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                  Brutto (wyliczona automatycznie)
                </label>
                <input
                  type="number"
                  step="0.01"
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                  value={form.amount_gross}
                  readOnly
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                  Termin płatności
                </label>
                <select
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                  value={form.payment_terms_days}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      payment_terms_days: parseInt(e.target.value),
                    })
                  }
                >
                  {PAYMENT_TERM_DAY_OPTIONS.map((d) => (
                    <option key={d} value={d}>
                      {d} dni
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                  Opis
                </label>
                <input
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  placeholder="Opcjonalny opis"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                  Typ dokumentu
                </label>
                <select
                  className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-700"
                  value={form.invoice_type}
                  onChange={(e) => {
                    const t = e.target.value as ClientInvoiceType;
                    setForm({
                      ...form,
                      invoice_type: t,
                      is_advance_invoice: t === "proforma_advance" ? true : form.is_advance_invoice,
                    });
                  }}
                >
                  {(Object.entries(INVOICE_TYPE_LABELS) as [ClientInvoiceType, string][]).map(([val, label]) => (
                    <option key={val} value={val}>{label}</option>
                  ))}
                </select>
              </div>
              {form.invoice_type === "regular" && (
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={form.is_advance_invoice}
                    onChange={(e) =>
                      setForm({ ...form, is_advance_invoice: e.target.checked })
                    }
                    className="w-4 h-4 accent-[var(--color-primary)]"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Faktura zaliczkowa
                  </span>
                </label>
              )}
              {formErr && <p className="text-xs text-red-600">{formErr}</p>}
              <Button loading={createMutation.isPending} onClick={handleCreate}>
                {form.invoice_number.trim()
                  ? "Utwórz fakturę PRZ"
                  : "Utwórz szkic faktury PRZ"}
              </Button>
            </div>
          </div>
        )}

        {/* Right panel — list */}
        <div className="flex-1 min-w-0">
          {/* View mode toggle */}
          <div className="flex justify-end mb-3">
            <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden text-xs font-medium">
              <button
                type="button"
                onClick={() => setViewMode("cards")}
                className={`flex items-center gap-1.5 px-3 py-1.5 transition-colors ${viewMode === "cards" ? "bg-[var(--color-primary)] text-white" : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"}`}
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>
                Karty
              </button>
              <button
                type="button"
                onClick={() => setViewMode("table")}
                className={`flex items-center gap-1.5 px-3 py-1.5 border-l border-gray-200 dark:border-gray-700 transition-colors ${viewMode === "table" ? "bg-[var(--color-primary)] text-white" : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"}`}
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 6h18M3 14h18M3 18h18M9 6v12M15 6v12" /></svg>
                Tabela
              </button>
            </div>
          </div>

          {isLoading ? (
            <PageLoader />
          ) : invoices.length === 0 ? (
            <p className="text-sm text-gray-400 dark:text-gray-500">
              Brak faktur PRZ.
            </p>
          ) : viewMode === "table" ? (
            <>
              {/* Desktop: inline table */}
              <div className="hidden lg:block">{invoicesTable}</div>
              {/* Mobile: CTA card opens landscape modal — inline table is unusable in portrait */}
              <button
                type="button"
                onClick={() => setLandscapeModalOpen(true)}
                className="lg:hidden w-full px-4 py-8 rounded-lg border-2 border-dashed border-[var(--color-primary)] bg-[var(--color-primary-dim)]/40 dark:bg-[var(--color-primary-hover)]/30 hover:bg-[var(--color-primary-dim)]/70 transition-colors flex flex-col items-center gap-2"
              >
                <svg className="w-8 h-8 text-[var(--color-primary)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 8V6a2 2 0 012-2h2M16 4h2a2 2 0 012 2v2M20 16v2a2 2 0 01-2 2h-2M8 20H6a2 2 0 01-2-2v-2" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12h6M12 9l3 3-3 3" />
                </svg>
                <span className="font-semibold text-[var(--color-primary)]">Otwórz pełną tabelę</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {sortedInvoices.length} faktur · widok poziomy
                </span>
              </button>
            </>
          ) : (
            <div className="flex flex-col gap-3">
                {invoices.map((inv) => {
                    const actions = availableActions(inv, user?.role);
                    const unapprovedDraft =
                      inv.status === "draft" && inv.director_approved !== true;
                    const hasActions =
                      canEdit && Object.values(actions).some(Boolean);
                    const showActionsBar =
                      hasActions || !!inv.planned_revenue_id;
                    const causesBadge =
                      user?.role === "director"
                        ? unapprovedDraft
                        : user?.role === "accountant"
                          ? inv.director_approved === true &&
                            inv.status === "draft"
                          : false;
                    return (
                      <div
                        key={inv.id}
                        className={`bg-white dark:bg-gray-800 rounded-lg border overflow-hidden ${causesBadge ? "border-l-[3px] border-l-amber-400 border-t-gray-200 border-r-gray-200 border-b-gray-200 dark:border-t-gray-700 dark:border-r-gray-700 dark:border-b-gray-700" : "border-gray-200 dark:border-gray-700"}`}
                      >
                        {/* Responsive card data */}
                        <div className="px-4 py-3">
                          {/* Top row: invoice number + company + file */}
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <Link
                                to={`/finances/invoices-prz/${inv.id}`}
                                className="font-medium text-[var(--color-primary)] hover:underline"
                              >
                                {inv.invoice_number || (
                                  inv.director_approved
                                    ? <span className="italic text-blue-500 dark:text-blue-400 font-normal">(szkic — zatwierdzony)</span>
                                    : <span className="italic text-gray-400 dark:text-gray-500 font-normal">(szkic — niezatwierdzony)</span>
                                )}
                              </Link>
                              <div className="flex items-center gap-1 mt-1 flex-wrap">
                                <Badge color={CLIENT_INVOICE_STATUS_COLORS[inv.status]}>
                                  {unapprovedDraft
                                    ? "Szkic — niezatw."
                                    : CLIENT_INVOICE_STATUS_LABELS[inv.status]}
                                </Badge>
                                {causesBadge && (
                                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                                    {user?.role === "director"
                                      ? "Wymaga zatwierdzenia"
                                      : "Gotowy do wystawienia"}
                                  </span>
                                )}
                                {inv.is_advance_invoice && (
                                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">
                                    Zaliczkowa
                                  </span>
                                )}
                                {inv.invoice_type === "proforma" && (
                                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                                    Proforma
                                  </span>
                                )}
                                {inv.invoice_type === "proforma_advance" && (
                                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
                                    Proforma zaliczkowa
                                  </span>
                                )}
                                {inv.director_approved === true && inv.status === "draft" && (
                                  <span className="text-xs text-green-600 dark:text-green-400 font-medium">· zatw.</span>
                                )}
                                {inv.status === "issued" && inv.sent_to_investor && (
                                  <span className="text-xs text-teal-600 dark:text-teal-400 font-medium">· wysłano</span>
                                )}
                                {inv.status === "issued" && !inv.sent_to_investor && (
                                  <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">· nie wysłano</span>
                                )}
                              </div>
                            </div>
                            <div className="text-xs text-right shrink-0">
                              <p className="text-gray-500 dark:text-gray-400 font-medium">{inv.company_name ?? "—"}</p>
                              {inv.invoice_managed_file_detail?.url ? (
                                <a
                                  href={inv.invoice_managed_file_detail.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-[var(--color-primary)] hover:underline"
                                >
                                  Pobierz
                                </a>
                              ) : (
                                <span className="text-gray-400 dark:text-gray-500">Brak pliku</span>
                              )}
                            </div>
                          </div>

                          {/* Project name */}
                          {inv.project_name && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5 leading-snug">{inv.project_name}</p>
                          )}

                          {/* Dates + amounts */}
                          <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1.5 text-xs">
                            <div>
                              <p className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">Wystawiona</p>
                              <p className="text-gray-700 dark:text-gray-300">{fmtDate(inv.invoice_date)}</p>
                            </div>
                            <div>
                              <p className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">Termin płatności</p>
                              <p className="text-gray-700 dark:text-gray-300">
                                {fmtDate(inv.payment_due_date)}
                                <span className="ml-1 text-gray-400">({inv.payment_terms_days}d)</span>
                              </p>
                            </div>
                            <div>
                              <p className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">Netto</p>
                              <p className="font-semibold text-gray-900 dark:text-gray-100 tabular-nums">
                                {formatAmount(inv.amount_net)}
                                <span className="font-normal text-gray-400 ml-1 text-[10px]">PLN</span>
                              </p>
                            </div>
                            <div>
                              <p className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">Brutto</p>
                              <p className="text-gray-600 dark:text-gray-400 tabular-nums">{formatAmount(inv.amount_gross)} PLN</p>
                            </div>
                          </div>

                          {/* Payment status + paid amount */}
                          <div className="mt-2 flex items-center gap-3 flex-wrap">
                            <Badge color={PAYMENT_STATUS_COLORS[inv.payment_status]}>
                              {PAYMENT_STATUS_LABELS[inv.payment_status]}
                            </Badge>
                            {inv.paid_amount && inv.paid_amount !== "0.00" && (
                              <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                                Zapłacono: {formatAmount(inv.paid_amount)} PLN
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Line items */}
                        {inv.line_items_count > 0 && (
                          <InlineLineItems invoiceId={inv.id} />
                        )}

                        {/* Actions bar */}
                        {showActionsBar && (
                          <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-4 py-2.5 flex items-center gap-2 flex-wrap">
                            {inv.planned_revenue_id && (
                              <Link
                                to="/finances/planned-revenues"
                                className="text-xs text-[var(--color-primary)] dark:text-indigo-400 hover:underline font-medium mr-1"
                                title={
                                  inv.planned_revenue_description ?? undefined
                                }
                              >
                                Pl. przychód: {inv.planned_revenue_description}
                              </Link>
                            )}
                            {inv.planned_revenue_id && hasActions && (
                              <span className="text-gray-300 dark:text-gray-600 select-none">
                                |
                              </span>
                            )}
                            {inv.status === "draft" && (
                              <Link
                                to={`/finances/invoices-prz/${inv.id}`}
                                className={`${chip} border-indigo-300 text-[var(--color-primary)] hover:bg-indigo-50`}
                              >
                                Opisz pozycje →
                              </Link>
                            )}
                            {actions.approve && inv.line_items_count > 0 && (
                              <button
                                onClick={() => approveMutation.mutate(inv.id)}
                                className={`${chip} border-green-300 text-green-700 hover:bg-green-50`}
                              >
                                Zatwierdź szkic
                              </button>
                            )}
                            {actions.revokeApproval && (
                              <button
                                onClick={() => revokeMutation.mutate(inv.id)}
                                className={`${chip} border-orange-300 text-orange-600 hover:bg-orange-50`}
                              >
                                Cofnij zatwierdzenie
                              </button>
                            )}
                            {actions.issue && (
                              <button
                                onClick={() => {
                                  setIssueModal({ invoice: inv });
                                  setIssueNumber(inv.invoice_number || "");
                                }}
                                className={`${chip} border-[var(--color-primary)] text-[var(--color-primary)] hover:bg-[var(--color-primary-dim)]`}
                              >
                                Wystaw fakturę
                              </button>
                            )}
                            {actions.issueBlocked && (
                              <button
                                disabled
                                title="Szkic niezatwierdzony przez dyrektora"
                                className={`${chip} border-gray-200 text-gray-400 cursor-not-allowed`}
                              >
                                Wystaw fakturę
                              </button>
                            )}
                            {actions.revertToDraft && (
                              <button
                                onClick={() =>
                                  updateMutation.mutate({
                                    id: inv.id,
                                    data: { status: "draft" },
                                  })
                                }
                                className={`${chip} border-gray-300 text-gray-600 dark:text-gray-400 dark:border-gray-600 hover:bg-gray-50`}
                              >
                                Cofnij do szkicu
                              </button>
                            )}
                            {actions.uploadFile && (
                              <button
                                onClick={() => handleFileClick(inv.id)}
                                className={`${chip} border-purple-300 text-purple-600 hover:bg-purple-50`}
                              >
                                {inv.invoice_managed_file_detail?.url ? "Zmień plik" : "Wgraj plik"}
                              </button>
                            )}
                            {actions.markSentToInvestor && (
                              <button
                                onClick={() =>
                                  updateMutation.mutate({
                                    id: inv.id,
                                    data: { sent_to_investor: true },
                                  })
                                }
                                className={`${chip} border-teal-300 text-teal-700 hover:bg-teal-50`}
                              >
                                Oznacz jako wysłane do inwestora
                              </button>
                            )}
                            {actions.revertSentToInvestor && (
                              <button
                                onClick={() =>
                                  updateMutation.mutate({
                                    id: inv.id,
                                    data: { sent_to_investor: false },
                                  })
                                }
                                className={`${chip} border-gray-300 text-gray-500 dark:text-gray-400 dark:border-gray-600 hover:bg-gray-50`}
                              >
                                Cofnij wysłanie
                              </button>
                            )}
                            {actions.payment && (
                              <button
                                onClick={() => openPayment(inv)}
                                className={`${chip} border-green-300 text-green-700 hover:bg-green-50`}
                              >
                                Zarejestruj płatność
                              </button>
                            )}
                            {actions.delete && (
                              <button
                                onClick={() => {
                                  if (confirm("Usunąć fakturę PRZ?"))
                                    deleteMutation.mutate(inv.id);
                                }}
                                className={`${chip} border-red-300 text-red-500 hover:bg-red-50`}
                              >
                                Usuń
                              </button>
                            )}
                            <span className="text-gray-200 dark:text-gray-700 select-none ml-auto">
                              |
                            </span>
                            <Link
                              to={`/finances/invoices-prz/${inv.id}`}
                              className="text-xs text-gray-400 dark:text-gray-500 hover:text-[var(--color-primary)] dark:hover:text-[var(--color-primary)] font-medium"
                            >
                              Szczegóły / historia →
                            </Link>
                          </div>
                        )}
                      </div>
                    );
                  })}
            </div>
          )}
        </div>
      </div>
      {/* end flex panels */}

      {/* Monthly stats table */}
      {monthlyStats.length > 0 && (
        <div className="mt-10">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">
            Zestawienie miesięczne faktur PRZ
          </h2>
          {/* Mobile: card list */}
          <div className="sm:hidden space-y-2">
            {monthlyStats.map((row) => (
              <div key={row.key} className="bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 capitalize mb-2">
                  {row.label}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <div className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">Wystawione</div>
                    <div className="text-xs font-semibold text-green-700 dark:text-green-400 tabular-nums">
                      {row.issued > 0 ? <>{formatAmount(row.issued.toFixed(2))}{jvFullNote(row.issued, row.issuedFull)}</> : <span className="text-gray-300">—</span>}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">+ zatw.</div>
                    <div className="text-xs font-semibold text-blue-700 dark:text-blue-400 tabular-nums">
                      {row.issuedPlusApproved > 0 ? <>{formatAmount(row.issuedPlusApproved.toFixed(2))}{jvFullNote(row.issuedPlusApproved, row.issuedPlusApprovedFull)}</> : <span className="text-gray-300">—</span>}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">+ wsz.</div>
                    <div className="text-xs font-semibold text-indigo-700 dark:text-indigo-400 tabular-nums">
                      {row.total > 0 ? <>{formatAmount(row.total.toFixed(2))}{jvFullNote(row.total, row.totalFull)}</> : <span className="text-gray-300">—</span>}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {/* Razem card */}
            <div className="bg-gray-50 dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-lg p-3">
              <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                Razem
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <div className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">Wystawione</div>
                  <div className="text-xs font-bold text-green-800 dark:text-green-300 tabular-nums">
                    {(() => { const o = monthlyStats.reduce((s, r) => s + r.issued, 0); const f = monthlyStats.reduce((s, r) => s + r.issuedFull, 0); return <>{formatAmount(o.toFixed(2))}{jvFullNote(o, f)}</>; })()}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">+ zatw.</div>
                  <div className="text-xs font-bold text-blue-800 dark:text-blue-300 tabular-nums">
                    {(() => { const o = monthlyStats.reduce((s, r) => s + r.issuedPlusApproved, 0); const f = monthlyStats.reduce((s, r) => s + r.issuedPlusApprovedFull, 0); return <>{formatAmount(o.toFixed(2))}{jvFullNote(o, f)}</>; })()}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">+ wsz.</div>
                  <div className="text-xs font-bold text-indigo-800 dark:text-indigo-300 tabular-nums">
                    {(() => { const o = monthlyStats.reduce((s, r) => s + r.total, 0); const f = monthlyStats.reduce((s, r) => s + r.totalFull, 0); return <>{formatAmount(o.toFixed(2))}{jvFullNote(o, f)}</>; })()}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Desktop: table */}
          <div className="hidden sm:block overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 dark:text-gray-400 text-xs uppercase tracking-wider">
                    Miesiąc
                  </th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-600 dark:text-gray-400 text-xs uppercase tracking-wider">
                    Wystawione (netto)
                  </th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-600 dark:text-gray-400 text-xs uppercase tracking-wider">
                    + szkice zatwierdzone
                  </th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-600 dark:text-gray-400 text-xs uppercase tracking-wider">
                    + szkice wszystkie
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
                {monthlyStats.map((row, i) => (
                  <tr
                    key={row.key}
                    className={`transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50 ${
                      i % 2 === 0
                        ? "bg-white dark:bg-gray-900/20"
                        : "bg-gray-50/50 dark:bg-gray-800/20"
                    }`}
                  >
                    <td className="px-4 py-2.5 text-gray-700 dark:text-gray-300 font-medium capitalize whitespace-nowrap">
                      {row.label}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap">
                      <CalcCell id={`monthly:${row.key}:0`} tableId="monthly" index={i} col={0} value={Number(row.issued.toFixed(2))} currency="PLN">
                        {row.issued > 0 ? (
                          <span className="text-sm font-semibold text-green-700 dark:text-green-400">
                            {formatAmount(row.issued.toFixed(2))}{" "}
                            <span className="text-xs font-normal text-gray-400">PLN</span>
                            {jvFullNote(row.issued, row.issuedFull)}
                          </span>
                        ) : (
                          <span className="text-gray-300 dark:text-gray-600 text-xs">—</span>
                        )}
                      </CalcCell>
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap">
                      <CalcCell id={`monthly:${row.key}:1`} tableId="monthly" index={i} col={1} value={Number(row.issuedPlusApproved.toFixed(2))} currency="PLN">
                        {row.issuedPlusApproved > 0 ? (
                          <span className="text-sm font-semibold text-blue-700 dark:text-blue-400">
                            {formatAmount(row.issuedPlusApproved.toFixed(2))}{" "}
                            <span className="text-xs font-normal text-gray-400">PLN</span>
                            {jvFullNote(row.issuedPlusApproved, row.issuedPlusApprovedFull)}
                          </span>
                        ) : (
                          <span className="text-gray-300 dark:text-gray-600 text-xs">—</span>
                        )}
                      </CalcCell>
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap">
                      <CalcCell id={`monthly:${row.key}:2`} tableId="monthly" index={i} col={2} value={Number(row.total.toFixed(2))} currency="PLN">
                        {row.total > 0 ? (
                          <span className="text-sm font-semibold text-indigo-700 dark:text-indigo-400">
                            {formatAmount(row.total.toFixed(2))}{" "}
                            <span className="text-xs font-normal text-gray-400">PLN</span>
                            {jvFullNote(row.total, row.totalFull)}
                          </span>
                        ) : (
                          <span className="text-gray-300 dark:text-gray-600 text-xs">—</span>
                        )}
                      </CalcCell>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                  <td className="px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Razem
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap">
                    <CalcCell id="monthly:total:0" tableId="monthly" index={monthlyStats.length} col={0} value={Number(monthlyStats.reduce((s, r) => s + r.issued, 0).toFixed(2))} currency="PLN">
                      <span className="text-sm font-bold text-green-800 dark:text-green-300">
                        {formatAmount(monthlyStats.reduce((s, r) => s + r.issued, 0).toFixed(2))}{" "}
                        <span className="text-xs font-normal text-gray-400">PLN</span>
                        {jvFullNote(monthlyStats.reduce((s, r) => s + r.issued, 0), monthlyStats.reduce((s, r) => s + r.issuedFull, 0))}
                      </span>
                    </CalcCell>
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap">
                    <CalcCell id="monthly:total:1" tableId="monthly" index={monthlyStats.length} col={1} value={Number(monthlyStats.reduce((s, r) => s + r.issuedPlusApproved, 0).toFixed(2))} currency="PLN">
                      <span className="text-sm font-bold text-blue-800 dark:text-blue-300">
                        {formatAmount(monthlyStats.reduce((s, r) => s + r.issuedPlusApproved, 0).toFixed(2))}{" "}
                        <span className="text-xs font-normal text-gray-400">PLN</span>
                        {jvFullNote(monthlyStats.reduce((s, r) => s + r.issuedPlusApproved, 0), monthlyStats.reduce((s, r) => s + r.issuedPlusApprovedFull, 0))}
                      </span>
                    </CalcCell>
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap">
                    <CalcCell id="monthly:total:2" tableId="monthly" index={monthlyStats.length} col={2} value={Number(monthlyStats.reduce((s, r) => s + r.total, 0).toFixed(2))} currency="PLN">
                      <span className="text-sm font-bold text-indigo-800 dark:text-indigo-300">
                        {formatAmount(monthlyStats.reduce((s, r) => s + r.total, 0).toFixed(2))}{" "}
                        <span className="text-xs font-normal text-gray-400">PLN</span>
                        {jvFullNote(monthlyStats.reduce((s, r) => s + r.total, 0), monthlyStats.reduce((s, r) => s + r.totalFull, 0))}
                      </span>
                    </CalcCell>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Landscape (rotated) fullscreen table modal — mobile */}
      {landscapeModalOpen && (
        <div className="fixed inset-0 z-50 bg-white dark:bg-gray-900">
          {/* Close button — always upper-right of physical screen */}
          <button
            type="button"
            onClick={() => setLandscapeModalOpen(false)}
            className="absolute top-3 right-3 z-20 w-10 h-10 rounded-full bg-gray-900/80 text-white flex items-center justify-center shadow-lg hover:bg-gray-900"
            aria-label="Zamknij"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Landscape orientation: show table directly, no rotation */}
          <div className="hidden landscape:block w-full h-full overflow-auto p-3 pt-14">
            <div className="mb-2 text-[10px] text-gray-400 uppercase tracking-wider">
              Pełna tabela — przewijaj poziomo
            </div>
            {invoicesTable}
          </div>

          {/* Portrait orientation: rotate content 90° so user tilts head / phone */}
          <div
            className="portrait:block hidden absolute top-0 left-0 origin-top-left bg-white dark:bg-gray-900"
            style={{
              width: "100dvh",
              height: "100dvw",
              transform: "translateX(100dvw) rotate(90deg)",
            }}
          >
            <div className="w-full h-full overflow-auto p-3 pt-12">
              <div className="mb-2 text-[10px] text-gray-400 uppercase tracking-wider">
                Tryb poziomy — obróć telefon
              </div>
              {invoicesTable}
            </div>
          </div>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
        onChange={handleFileChange}
      />

      {/* Issue modal */}
      {issueModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 w-full max-w-sm">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
              Wystaw fakturę PRZ
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              Podaj numer i dołącz plik PDF faktury przed wystawieniem.
            </p>
            <div className="flex flex-col gap-3">
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                  Nr faktury *
                </label>
                <input
                  type="text"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="np. FV/2026/001"
                  value={issueNumber}
                  onChange={(e) => setIssueNumber(e.target.value)}
                />
                {!issueNumber.trim() && (
                  <p className="text-xs text-red-500 mt-1">
                    Wymagany przed wystawieniem.
                  </p>
                )}
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                  Plik faktury *
                </label>
                <FileDropZone
                  files={issueFile ? [issueFile] : []}
                  onFilesChange={(fs) => setIssueFile(fs[0] ?? null)}
                  accept=".pdf,.jpg,.jpeg,.png"
                />
                {!issueFile && (
                  <p className="text-xs text-red-500 mt-1">
                    Wymagany przed wystawieniem.
                  </p>
                )}
              </div>
              <div className="flex gap-2 mt-1">
                <Button
                  size="sm"
                  loading={updateMutation.isPending}
                  onClick={handleIssue}
                  disabled={!issueFile || !issueNumber.trim()}
                >
                  Wystaw fakturę
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setIssueModal(null);
                    setIssueFile(null);
                    setIssueNumber("");
                  }}
                >
                  Anuluj
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment modal */}
      {paymentModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 w-full max-w-sm">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
              Zarejestruj wpływ — {paymentModal.invoice.invoice_number}
            </h3>
            {paymentModal.invoice.paid_amount !== "0.00" && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                Dotychczas zapłacono:{" "}
                {formatAmount(paymentModal.invoice.paid_amount)} PLN z{" "}
                {formatAmount(paymentModal.invoice.amount_gross)} PLN brutto
              </p>
            )}
            <div className="flex flex-col gap-3 mt-3">
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                  Kwota wpływu brutto (PLN) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                  value={payForm.new_payment_amount}
                  onChange={(e) =>
                    setPayForm({
                      ...payForm,
                      new_payment_amount: e.target.value,
                    })
                  }
                  placeholder="0.00"
                  autoFocus
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                  Wpłata netto (obliczona automatycznie)
                </label>
                <input
                  type="text"
                  readOnly
                  disabled
                  className="w-full rounded px-2 py-1.5 text-sm bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
                  value={(() => {
                    const gross = parseFloat(payForm.new_payment_amount);
                    const invGross = parseFloat(
                      paymentModal.invoice.amount_gross,
                    );
                    const invNet = parseFloat(paymentModal.invoice.amount_net);
                    if (!isNaN(gross) && gross > 0 && invGross > 0)
                      return formatAmount((gross * invNet) / invGross) + " PLN";
                    return "—";
                  })()}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                  Data wpływu na konto *
                </label>
                <DatePicker
                  value={payForm.payment_date}
                  onChange={(v) =>
                    setPayForm({ ...payForm, payment_date: v })
                  }
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                  Komentarz
                </label>
                <input
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                  value={payForm.payment_comment}
                  onChange={(e) =>
                    setPayForm({ ...payForm, payment_comment: e.target.value })
                  }
                  placeholder="Opcjonalny komentarz"
                />
              </div>
              <div className="flex gap-2 mt-1">
                <Button
                  size="sm"
                  loading={updatePayment.isPending}
                  onClick={handleUpdatePayment}
                  disabled={
                    !payForm.new_payment_amount || !payForm.payment_date
                  }
                >
                  Zapisz
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setPaymentModal(null)}
                >
                  Anuluj
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </SheetCalcProvider>
  );
}
